import { useCallback, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { IMAGE_LIST_FAILED, pickDisplayedImage } from '@/modules/uiImages/pickDisplayedImage';
import type { StoryEditUIImages } from '@/hooks/useStoryEditData';

export type StoryImageCategory = 'SceneLandscape' | 'ScanImage' | 'ImageAnchor';
type Crop = { x: number; y: number; width: number; height: number };

const IMAGE_SAVE_FAILED = "We couldn't save the image. Please try again.";
const IMAGE_REMOVE_FAILED = "We couldn't remove the image. Please try again.";

const LABEL: Record<StoryImageCategory, string> = {
  SceneLandscape: 'Story cover image',
  ScanImage: 'Scan image',
  ImageAnchor: 'Image',
};

function shown(category: StoryImageCategory, url: string | null, crop: Crop | null | undefined): Partial<StoryEditUIImages> {
  if (category === 'SceneLandscape') return { coverImageUrl: url, coverImageCrop: crop ?? null };
  if (category === 'ScanImage') return { scanImageUrl: url, scanImageCrop: crop ?? null };
  return { anchorImageUrl: url, anchorImageCrop: crop ?? null };
}

function cleared(category: StoryImageCategory): Partial<StoryEditUIImages> {
  if (category === 'SceneLandscape') return { coverImageUrl: null, coverImageOriginalUrl: null, coverImageCrop: null };
  if (category === 'ScanImage') return { scanImageUrl: null, scanImageOriginalUrl: null, scanImageCrop: null };
  return { anchorImageUrl: null, anchorImageOriginalUrl: null, anchorImageCrop: null };
}

interface Args {
  sceneOriginId: string | null | undefined;
  projectId: string | null | undefined;
  userId: string | null | undefined;
  setUiImages: Dispatch<SetStateAction<StoryEditUIImages>>;
  fetchUIImages: (sceneOriginId: string) => Promise<boolean>;
  fetchVersionHistory: (sceneOriginId: string) => Promise<void>;
}

export function useStoryImageWrites({ sceneOriginId, projectId, userId, setUiImages, fetchUIImages, fetchVersionHistory }: Args) {
  const services = useServices();

  const displayedRow = useCallback(async (storyId: string, category: StoryImageCategory) => {
    const existing = await services.uiImages.getUIImages({ sceneOriginId: storyId, imageCategory: category });
    if (!existing.success) throw new Error(IMAGE_LIST_FAILED);
    return pickDisplayedImage(existing.data ?? [], category);
  }, [services.uiImages]);

  const save = useCallback(async (
    storyId: string,
    owner: string,
    category: StoryImageCategory,
    fields: { imageOriginalUrl: string; imageCroppedUrl?: string; imageMetadata?: { crop: Crop } },
  ) => {
    const row = await displayedRow(storyId, category);
    const result = row
      ? await services.uiImages.updateUIImage(row.imageId, fields)
      : await services.uiImages.createUIImage({ sceneOriginId: storyId, projectInstanceId: projectId ?? undefined, imageCategory: category, userId: owner, ...fields });
    if (!result.success) throw new Error(IMAGE_SAVE_FAILED);
  }, [displayedRow, projectId, services.uiImages]);

  const onImageSelect = useCallback(async (category: StoryImageCategory, imageUrl: string, cropData?: Crop) => {
    if (!sceneOriginId || !userId) return;
    try {
      if (imageUrl) {
        await save(sceneOriginId, userId, category, { imageOriginalUrl: imageUrl, imageMetadata: cropData ? { crop: cropData } : undefined });
        setUiImages((prev) => ({ ...prev, ...shown(category, imageUrl, cropData) }));
      } else {
        const row = await displayedRow(sceneOriginId, category);
        if (row) {
          const result = await services.uiImages.deleteUIImage(row.imageId);
          if (!result.success) throw new Error(IMAGE_REMOVE_FAILED);
        }
        setUiImages((prev) => ({ ...prev, ...cleared(category) }));
      }
      await fetchUIImages(sceneOriginId);
      void fetchVersionHistory(sceneOriginId);
      toast.success(imageUrl ? `${LABEL[category]} updated successfully` : `${LABEL[category]} removed successfully`);
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error(error instanceof Error ? error.message : IMAGE_SAVE_FAILED);
    }
  }, [sceneOriginId, userId, save, displayedRow, services.uiImages, setUiImages, fetchUIImages, fetchVersionHistory]);

  const onCropApplied = useCallback(async (
    category: StoryImageCategory,
    data: { imageOriginalUrl: string; imageCroppedUrl: string; crop: Crop },
  ) => {
    if (!sceneOriginId || !userId) return;
    try {
      await save(sceneOriginId, userId, category, {
        imageOriginalUrl: data.imageOriginalUrl,
        imageCroppedUrl: data.imageCroppedUrl,
        imageMetadata: { crop: data.crop },
      });
      setUiImages((prev) => ({ ...prev, ...shown(category, data.imageCroppedUrl, data.crop) }));
      await fetchUIImages(sceneOriginId);
      toast.success('Image uploaded and cropped successfully');
    } catch (error) {
      console.error('Error updating crop:', error);
      toast.error(error instanceof Error ? error.message : IMAGE_SAVE_FAILED);
    }
  }, [sceneOriginId, userId, save, setUiImages, fetchUIImages]);

  return { onImageSelect, onCropApplied };
}
